import { useEffect, useRef, useState } from "react";
import { C } from "../theme/colors";
import DonutChartV2 from "../components/DonutChartV2";

const COURSE_API_URL = "https://ccapi.rapifuzz.in:8443/api/training/professional-development-course-in-cybersecurity/";
const TRAINING_INTEREST_API_URL = "https://ccapi.rapifuzz.in:8443/api/training-interest/";
const API_BASE = "https://ccapi.rapifuzz.in:8443";

// Resolves relative media paths ("/media/...") returned by the API against API_BASE.
// Leaves absolute URLs (http/https) untouched. Returns null for empty/undefined.
function mediaUrl(path) {
  if (!path) return null;
  return /^https?:\/\//i.test(path) ? path : `${API_BASE}${path}`;
}

// Pulls a YouTube video ID out of either an /embed/<id> or watch?v=<id> style URL.
function extractYoutubeId(url) {
  if (!url) return null;
  const embedMatch = url.match(/embed\/([a-zA-Z0-9_-]{6,})/);
  if (embedMatch) return embedMatch[1];
  const watchMatch = url.match(/[?&]v=([a-zA-Z0-9_-]{6,})/);
  if (watchMatch) return watchMatch[1];
  return null;
}

// Small reusable carousel: shows one item at a time with prev/next + dots, auto-advances,
// pauses on hover/touch so the user can read the current slide.
function Carousel({ items, renderItem, height = 320, autoPlay = true, interval = 1800, dots = false }) {
  const [i, setI] = useState(0);
  const len = items.length;

  // Runs continuously on a plain interval — no hover/touch pause, so it never
  // appears to "stop" just because the cursor happens to sit over it.
  useEffect(() => {
    if (!autoPlay || len <= 1) return undefined;
    const id = setInterval(() => {
      setI(prev => (prev + 1) % len);
    }, interval);
    return () => clearInterval(id);
  }, [autoPlay, len, interval]);

  if (!len) return null;
  const prev = () => setI(p => (p - 1 + len) % len);
  const next = () => setI(p => (p + 1) % len);

  // Dot-style navigation (matches the PeekSlider dots elsewhere on the page) — rendered
  // below the slide instead of the arrow+counter overlay.
  if (dots) {
    return (
      <div>
        <div style={{borderRadius:12,overflow:"hidden",height,background:C.bgAlt}}>
          {renderItem(items[i], i)}
        </div>
        {len > 1 && (
          <div style={{display:"flex",justifyContent:"center",gap:8,marginTop:16}}>
            {items.map((_,idx) => (
              <button key={idx} onClick={()=>setI(idx)} aria-label={`Go to slide ${idx+1}`}
                style={{width:i===idx?18:8,height:8,borderRadius:4,border:"none",background:i===idx?C.navy:"#CBD5E1",cursor:"pointer",transition:"width .2s,background .2s",padding:0}}/>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{position:"relative"}}>
      <div style={{borderRadius:12,overflow:"hidden",height,background:C.bgAlt}}>
        {renderItem(items[i], i)}
      </div>
      {len > 1 && (
        <div style={{position:"absolute",bottom:14,left:"50%",transform:"translateX(-50%)",display:"flex",alignItems:"center",gap:10,background:"rgba(8,21,64,0.75)",borderRadius:20,padding:"6px 14px"}}>
          <button onClick={prev} style={{background:"none",border:"none",color:"#fff",cursor:"pointer",fontSize:14,padding:0}}>‹</button>
          <span style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:12,color:"#fff",minWidth:36,textAlign:"center"}}>{i+1} / {len}</span>
          <button onClick={next} style={{background:"none",border:"none",color:"#fff",cursor:"pointer",fontSize:14,padding:0}}>›</button>
        </div>
      )}
    </div>
  );
}

// Peek-slider: shows cards side-by-side with the next one peeking at the edge + dot navigation.
// Auto-advances on an interval and pauses while the user is interacting with it.
function PeekSlider({ items, renderItem, cardWidth = "calc(50% - 9px)", cardClassName = "", autoPlay = true, interval = 2200 }) {
  const trackRef = useRef(null);
  const [active, setActive] = useState(0);
  const len = items.length;

  const scrollToIndex = (idx) => {
    const track = trackRef.current;
    const card = track && track.children[idx];
    if (card) track.scrollTo({ left: card.offsetLeft, behavior: "smooth" });
  };

  const goTo = (idx) => {
    setActive(idx);
    scrollToIndex(idx);
  };

  // Runs continuously on a plain interval — no hover/touch pause, so it never
  // appears to "stop" just because the cursor happens to sit over it.
  useEffect(() => {
    if (!autoPlay || len <= 1) return undefined;
    const id = setInterval(() => {
      setActive(prev => {
        const next = (prev + 1) % len;
        scrollToIndex(next);
        return next;
      });
    }, interval);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPlay, len, interval]);

  if (!len) return null;
  return (
    <div>
      <div ref={trackRef} className="peek-track" style={{display:"flex",gap:18,overflowX:"auto",scrollSnapType:"x mandatory",paddingBottom:4}}>
        {items.map((item,i) => (
          <div key={i} className={cardClassName} style={{flex:`0 0 ${cardWidth}`,scrollSnapAlign:"start"}}>
            {renderItem(item,i)}
          </div>
        ))}
      </div>
      {len > 1 && (
        <div style={{display:"flex",justifyContent:"center",gap:8,marginTop:16}}>
          {items.map((_,i) => (
            <button key={i} onClick={()=>goTo(i)} aria-label={`Go to slide ${i+1}`}
              style={{width:active===i?18:8,height:8,borderRadius:4,border:"none",background:active===i?C.navy:"#CBD5E1",cursor:"pointer",transition:"width .2s,background .2s",padding:0}}/>
          ))}
        </div>
      )}
    </div>
  );
}

// JS-driven sticky wrapper. CSS `position: sticky` silently stops working the moment ANY
// ancestor between this element and the scrolling container has overflow:hidden/auto/scroll —
// very common in app shells/layouts — so this computes the "stuck" state on scroll instead and
// uses position:fixed, which isn't affected by that. `boundsRef` should point at the element
// whose height defines how far the panel is allowed to travel (here, the two-column grid).
function StickyPanel({ children, boundsRef, topOffset = 80, disableBelow = 1024 }) {
  const outerRef = useRef(null);
  const innerRef = useRef(null);
  const [style, setStyle] = useState(null);
  const [placeholder, setPlaceholder] = useState(0);

  useEffect(() => {
    const update = () => {
      const bounds = boundsRef.current;
      const outer = outerRef.current;
      const inner = innerRef.current;
      if (!bounds || !outer || !inner) return;

      if (window.innerWidth < disableBelow) {
        setStyle(null);
        setPlaceholder(0);
        return;
      }

      const boundsRect = bounds.getBoundingClientRect();
      const outerRect = outer.getBoundingClientRect();
      const innerHeight = inner.offsetHeight;

      if (boundsRect.top <= topOffset && boundsRect.bottom > innerHeight + topOffset) {
        // Stuck to the top of the viewport while the column scrolls past it.
        // maxHeight + overflowY keep the form fully reachable (incl. the Submit
        // button) even when the viewport is too short to fit it all at once —
        // without this a fixed-position panel taller than the screen simply
        // clips at the bottom with no way to scroll to what's cut off.
        const bottomGap = 24;
        setStyle({
          position:"fixed", top:topOffset, left:outerRect.left, width:outerRect.width, zIndex:20,
          maxHeight:`calc(100vh - ${topOffset + bottomGap}px)`, overflowY:"auto",
        });
        setPlaceholder(innerHeight);
      } else if (boundsRect.bottom <= innerHeight + topOffset) {
        // Column is ending — pin to the bottom of the column instead of overshooting it
        setStyle({ position:"absolute", bottom:0, left:0, width:outerRect.width, zIndex:20 });
        setPlaceholder(innerHeight);
      } else {
        setStyle(null);
        setPlaceholder(0);
      }
    };

    update();
    window.addEventListener("scroll", update, { passive:true });
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [boundsRef, topOffset, disableBelow]);

  return (
    <div ref={outerRef} style={{position:"relative"}}>
      {placeholder > 0 && <div style={{height:placeholder}} aria-hidden="true" />}
      <div ref={innerRef} style={style || {}}>
        {children}
      </div>
    </div>
  );
}

export default function CyberCourseDetailPage({ onBack }) {
  const [openModule, setOpenModule] = useState(null);
  const [openFaq, setOpenFaq]       = useState(null);
  const [videoModal, setVideoModal] = useState(null);
  const [submitted, setSubmitted]   = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [form, setForm]             = useState({ name:"", org:"", desig:"", email:"", phone:"", orgType:"" });
  const [apiRoot, setApiRoot]       = useState(null); // top-level fields
  const [apiData, setApiData]       = useState(null); // data[0]
  const gridRef = useRef(null); // bounds the sticky sidebar's travel range

  useEffect(() => {
    let cancelled = false;

    fetch(COURSE_API_URL)
      .then(res => {
        if (!res.ok) throw new Error(`Request failed (${res.status})`);
        return res.json();
      })
      .then(json => {
        if (cancelled) return;
        setApiRoot(json || null);
        const record = Array.isArray(json?.data) ? json.data[0] : null;
        if (record) setApiData(record);
      })
      .catch(() => {
        // Silently keep static fallback data on any failure
      });

    return () => { cancelled = true; };
  }, []);

  // ── Hero fields (root-level API fields) ──
  const badgeText = apiRoot?.course_offer || "Skill Development Program · Cohort 2";
  const heroTitle = apiRoot?.course_title || "Professional Development Course in Cybersecurity";
  const heroSubtitle = apiRoot?.course_desc
    || "An eight-week online certification program for defence, government, and industry professionals";

  const partners = apiData?.tag_first?.length
    ? apiData.tag_first.map(t => t.tag_name)
    : ["CRENS","BITS Pilani","CERT-In (MeitY)","Rapifuzz"];

  const highlightTags = apiData?.tag_second?.length
    ? apiData.tag_second.map(t => t.tag_sec_name)
    : ["8 Weeks Online","90 Professionals (Batch 1)","Live Lab Sessions","Certificate by CRENS & CERT-In"];

  // ── Intro / "Why" section ──
  // API actually returns `leader_heading` / `leader_desc` on the data[0] record
  // (not `sub_title` / `sub_desc`).
  const whySectionTitle = apiData?.leader_heading || "Academia. Government. Industry. United to Build Cybersecurity Leaders";
  const whySectionDesc = apiData?.leader_desc
    || "Advance your cybersecurity expertise through a professional program jointly offered by the Centre for Research Excellence in National Security (CRENS), BITS Pilani, in collaboration with CERT-In, with Rapifuzz® as Technology Partner. Designed for professionals across government, defense, critical infrastructure, and industry, the program combines academic rigor, national cybersecurity perspectives, and practical, hands-on learning. Participants gain a strong foundation in modern cybersecurity concepts while exploring real-world attack scenarios, security frameworks, incident response strategies, and emerging threats through expert-led sessions.";

  // leader_desc from the API is HTML (has <p>/<strong> tags) — flag it so the renderer
  // below uses dangerouslySetInnerHTML instead of printing the tags as plain text.
  const whySectionDescIsHtml = !!apiData?.leader_desc;

  // Logos shown directly under the "Academia. Government. Industry." heading.
  // company_logo is a TOP-LEVEL array (apiRoot.company_logo), not nested inside data[0],
  // and each item only carries a `logo` image path (no name) — so these render as image
  // badges instead of text pills.

const whySectionLogos = apiData?.company_logo?.length
    ? apiData.company_logo.map(c => ({ image: mediaUrl(c.logo) }))
    : [{ name: "BITSs Pilani" }, { name: "certin" }, { name: "RAPIFUZZ" }];

  // Photo collage — API's `gallery` array feeds this (first two images); static placeholders if empty
// Photo collage — data[0]'s single `leader_image` field is the primary photo for this
// section; `gallery` (if present) supplies a second overlapping photo. Static placeholders
// if neither is available.

const staticIntroGallery = [
    { src:"https://placehold.co/700x400/0F2557/FFFFFF?text=Cohort+Group+Photo" },
  ];
  const leaderImageUrl = mediaUrl(apiData?.leader_image);
  const introGallery = leaderImageUrl
    ? [{ src: leaderImageUrl }]
    : staticIntroGallery;

  const stats = apiData?.national_securitys?.length
    ? apiData.national_securitys.map(s => [s.tag_name, s.tag_desc])
    : [["25%","Year-on-Year Growth in cybersecurity job openings in India"],["3.5M+","Unfilled Cybersecurity Roles globally (Cybersecurity Ventures)"],["$10.5–11.5 billion","By 2026, it is projected to growing at a 15–20% CAGR"]];
  const statColors = ["#16A34A", "#2563EB", "#B45309"];
  const statBgs = ["#F0FDF4", "#EFF6FF", "#FFFBEB"];

  // ── Who Should Attend ──
  const attendIcons = ["🛡","🏛","⚙","🔒","🌐","🎓"];
  const staticAttends = [
    {icon:"🛡",title:"Defence Personnel",   desc:"Army, Navy, Air Force officers working with IT systems"},
    {icon:"🏛",title:"Government Officials",desc:"IAS/IPS/IFS officers handling digital governance"},
    {icon:"⚙",title:"Technical Officers",  desc:"Engineers in DRDO, ISRO, and PSU environments"},
    {icon:"🔒",title:"Security Analysts",   desc:"SOC and cyber incident response professionals"},
    {icon:"🌐",title:"Network Engineers",   desc:"IT infrastructure teams in critical organisations"},
    {icon:"🎓",title:"Security Researchers",desc:"Academic researchers entering the security domain"},
  ];
  const attends = apiData?.attends?.length
    ? apiData.attends.map((a,i) => ({ title: a.att_title, desc: a.att_desc, image: mediaUrl(a.image), icon: attendIcons[i % attendIcons.length] }))
    : staticAttends;

  // ── Career Path section ("Where this program / Can Take You") ──
  const careerEyebrow = apiData?.program_heading || "Your Career Path";
  const careerHeading = apiData?.program_sub_heading || "After Certification";
  const careerDescParas = apiData?.where_program_desc
    ? [apiData.where_program_desc]
    : [
        "This certification opens doors to some of the most in-demand and high-paying roles in the cybersecurity industry. Whether you're starting fresh or upskilling, you'll gain the expertise to step into these rewarding career paths.",
        "From entry-level analyst to advanced leadership roles like Security Architect or even CISO, this course sets the foundation for limitless career growth.",
      ];
  const staticCareerPaths = [
    { label:"Ethical Hacker / Penetration Tester", highlight:true },
    { label:"Security Consultant", highlight:false },
    { label:"Cybersecurity Analyst", highlight:false },
    { label:"Cloud Security Architect", highlight:true },
    { label:"Incident Response Specialist", highlight:false },
    { label:"Network Security Engineer", highlight:false },
  ];
  const careerPaths = apiData?.where_program?.length
    ? apiData.where_program.map((w,i) => ({ label: w.title, highlight: i % 3 === 0 }))
    : staticCareerPaths;

  // ── Faculty ──
  const facultyHeadingLine1 = "World-Class Faculty Driving";
  const facultyHeadingLine2 = apiData?.excellence_heading || "Cybersecurity Excellence";
  const staticFaculty = [
    {
      name:"Prof. M Rama Manohara Babu",
      title:"Professor of Practice, Mechanical Engineering, Head-CRENS",
      photo:"https://placehold.co/120x120/E8EEF9/1A3A8F?text=Photo",
      bio:"A former Distinguished Scientist and Director from Advanced Systems Laboratory, DRDO, who upon retirement joined BITS Pilani, Hyderabad Campus as Professor of Practice. He is now heading the Centre for Research Excellence in National Security (CRENS), established at BITS Pilani, dedicated to bolstering India's national security infrastructure by fostering collaboration between academia, industry, and government.",
    },
    {
      name:"Dr. Pranav Mothabhau Pawar",
      title:"Assistant Professor, Department of Computer Science",
      photo:"https://placehold.co/120x120/E8EEF9/1A3A8F?text=Photo",
      bio:"Dr. Pranav M. Pawar graduated in Computer Engineering from Dr. Babasaheb Ambedkar Technological University, Maharashtra, India, in 2005, received a Master in Computer Engineering from Pune University in 2007, and received his PhD in Wireless Communications from Aalborg University, Denmark in 2016. His PhD thesis on secure medium access control for wireless sensor networks received a nomination for Best Thesis Award from Aalborg University, Denmark.",
    },
  ];
  const faculty = apiData?.excellence_prof?.length
    ? apiData.excellence_prof.map(p => ({
        name: p.name,
        title: p.designation,
        bio: p.description,
        photo: mediaUrl(p.image) || "https://placehold.co/120x120/E8EEF9/1A3A8F?text=Photo",
        companyLogo: mediaUrl(p.company_logo),
      }))
    : staticFaculty;

  // ── Success gallery — API's `gallery` array (all images) ──
  const staticSuccessGallery = [
    "https://placehold.co/900x420/0F2557/FFFFFF?text=Cohort+Session",
    "https://placehold.co/900x420/1A3A8F/FFFFFF?text=Group+Photo",
    "https://placehold.co/900x420/2563EB/FFFFFF?text=Panel+Discussion",
    "https://placehold.co/900x420/0F2557/FFFFFF?text=Certificate+Distribution",
  ];
  const successGallery = apiData?.gallery?.length
    ? apiData.gallery.map(g => mediaUrl(g.image)).filter(Boolean)
    : staticSuccessGallery;

  // ── Testimonials — API's `youtube` array ──
  const videoHeading = apiData?.video_heading || "Our Community Speaks";
  const staticTestimonials = [
    { thumb:"https://placehold.co/900x420/111827/FFFFFF?text=▶+Watch+Testimonial+1", youtubeId: null },
    { thumb:"https://placehold.co/900x420/1F2937/FFFFFF?text=▶+Watch+Testimonial+2", youtubeId: null },
    { thumb:"https://placehold.co/900x420/0F2557/FFFFFF?text=▶+Watch+Testimonial+3", youtubeId: null },
    { thumb:"https://placehold.co/900x420/1A3A8F/FFFFFF?text=▶+Watch+Testimonial+4", youtubeId: null },
  ];
  const testimonials = apiData?.youtube?.length
    ? apiData.youtube.map(y => {
        const id = extractYoutubeId(y.video_url);
        return {
          youtubeId: id,
          thumb: id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : "https://placehold.co/900x420/111827/FFFFFF?text=▶+Watch+Testimonial",
        };
      })
    : staticTestimonials;

  // ── Speakers ("organization" images + organization_desc HTML with the topics list) ──
  const organizationTitleLine1 = "Speakers From India's Most";
  const organizationTitleLine2 = apiData?.organization_title || "Prestigious Organizations";
  const organizationDescHtml = apiData?.organization_desc || null;
  const staticSpeakerLogos = ["CERT-In", "Nexova", "DSCI", "CSCC Labs", "Globals", "Team Cymru"];
  const speakerLogos = apiData?.organization?.length
    ? apiData.organization.map(o => ({ image: mediaUrl(o.image) }))
    : staticSpeakerLogos.map(name => ({ name }));

  // ── How the Program is Delivered ──
  const deliveryTitle = apiData?.program_title || "How the Program is Delivered";
  const deliveryIcons = ["📶","👥","🖥"];
  const staticDeliveryFeatures = [
    { icon:"📶", title:"Online Self-Paced + Live", desc:"Pre-recorded lectures + weekly live sessions with subject experts" },
    { icon:"👥", title:"Inter-Campus Faculty",     desc:"Experts from all BITS Pilani campuses and partner institutions" },
    { icon:"🖥", title:"Cyberkshetra Labs",         desc:"Hands-on virtual lab environment for practical security exercises" },
  ];
  const deliveryFeatures = apiData?.programs?.length
    ? apiData.programs.map((p,i) => ({ title: p.pro_title, desc: p.pro_desc, image: mediaUrl(p.image), icon: deliveryIcons[i % deliveryIcons.length] }))
    : staticDeliveryFeatures;
  const invitedTalksNote = apiData?.program_desc
    || "5 invited talks by leading industry experts, including the Director General of CERT-In, enriched the Batch 1 curriculum.";

  // ── Tools & Skills ──
  const staticToolsList = [
    { name:"WIRESHARK",  icon:"🦈", color:"#1679BE", logo:"https://cdn.simpleicons.org/wireshark/ffffff" },
    { name:"NMAP",       icon:"👁", color:"#4682B4" },
    { name:"Nikto",      icon:"🤖", color:"#111827" },
    { name:"metasploit", icon:"🛡", color:"#2F2A70", logo:"https://cdn.simpleicons.org/metasploit/2F2A70" },
    { name:"ChatGPT",    icon:"✳", color:"#10A37F", logo:"https://cdn.simpleicons.org/openai/10A37F" },
    { name:"SHODAN",     icon:"⬤", color:"#D6002B" },
    { name:"Nessus",     icon:"➰", color:"#00A99D" },
    { name:"AIRCRACK-NG",icon:"📡", color:"#111827" },
    { name:"KALI LINUX", icon:"🐉", color:"#000000", logo:"https://cdn.simpleicons.org/kalilinux/000000" },
  ];
  // API's `tool` records only carry an image (no name/color), so each becomes an image-only card
  const toolsList = apiData?.tool?.length
    ? apiData.tool.map(t => ({ logo: mediaUrl(t.image) }))
    : staticToolsList;

  const staticSkillsList = ["Operating Systems Fundamentals","System Architecture","Virtual Memory Concepts","Networking Concept","Network Forensics","Threats and Vulnerabilities","Ethical Hacking","MITRE Framework","Identity Management IAM","SIEM Systems","Firewalls & Security Protocols","Network Protocols"];
  const skillsList = apiData?.skill?.length
    ? apiData.skill.map(s => s.title)
    : staticSkillsList;

  // ── Graduation ceremony (campus_* fields) ──
  const campusTitle = apiData?.campus_title || "On-Campus Graduation Ceremony At BITS Pilani, Hyderabad Campus";
  const campusSubheading = apiData?.campus_subheading || "Network With Faculty & Industry Peers";
  const campusDesc = apiData?.campus_desc
    || "Celebrate the successful completion of your learning journey at the BITS Pilani, Hyderabad Campus. Participants will be awarded their certificates during the on-campus graduation ceremony, providing an opportunity to connect with faculty, industry experts, and fellow professionals.";
  const staticGraduationGallery = [
    "https://placehold.co/500x360/0F2557/FFFFFF?text=Graduation+Ceremony",
    "https://placehold.co/500x360/1A3A8F/FFFFFF?text=Certificate+Handover",
  ];
  const apiGraduationGallery = [apiData?.campus_image1, apiData?.campus_image2].map(mediaUrl).filter(Boolean);
  const graduationGallery = apiGraduationGallery.length ? apiGraduationGallery : staticGraduationGallery;

  // ── Fee ──
  const feeTitle = apiData?.fee_title || "The Time to Invest in Your Career Is Now";
  const feeSectionDesc = apiData?.fee_desc || "A small investment today can unlock a high-demand cybersecurity career with limitless opportunities.";
  const feeAmount = apiData?.fee || "40,000";
  const batchNote = apiData?.batch_tags || "Next batch starts in October 2026 (tentative).";
  const feeBoxNote = "Secure your seat at the inaugural program fee.";

  const cyberkshetraFeaturesFallback = [
    "Pro Labs – SCADA, IoT & Simulated Network Devices",
    "Monitoring & Managing Events",
    "Live-fire Scenario with Offensive & Defensive CVE Labs",
    "Comprehensive Guides & Walkthrough",
    "Smart Suggestion & Technique wise Skill Progression",
  ];

  // ── Curriculum ──
  const curriculumTitle = apiData?.curriculum_title || "Course Curriculum — 8 Modules + Capstone";
  const curriculumDesc = apiData?.curriculum_desc || "Hands-on lab sessions complement every module, delivered on the Cyberkshetra platform.";
  const staticModules = [
    { title:"Module 1: Foundations of Cybersecurity", desc:"CIA triad, attack surfaces, threat landscape, cybersecurity frameworks (NIST, ISO 27001), and India-specific regulatory requirements." },
    { title:"Module 2: Network Security & Defence",   desc:"Firewalls, IDS/IPS, VPNs, zero-trust architecture, network traffic analysis, and defence-grade network hardening." },
    { title:"Module 3: Threat Intelligence & Analytics", desc:"OSINT, threat actors, TTPs (Tactics, Techniques & Procedures), MITRE ATT&CK framework, and threat-intel platforms." },
    { title:"Module 4: Ethical Hacking & Penetration Testing", desc:"Reconnaissance, vulnerability scanning, exploitation techniques, web app pen testing, and responsible disclosure." },
    { title:"Module 5: Incident Response & Forensics", desc:"IR lifecycle, evidence collection, memory forensics, malware analysis, and post-incident reporting." },
    { title:"Module 6: Cryptography & PKI",           desc:"Symmetric/asymmetric encryption, digital signatures, PKI infrastructure, and post-quantum cryptography basics." },
    { title:"Module 7: Cloud & OT/ICS Security",      desc:"Cloud shared responsibility model, container security, SCADA/ICS vulnerabilities, and critical infrastructure protection." },
    { title:"Module 8: Governance, Risk & Compliance", desc:"Risk management frameworks, cybersecurity audits, policy writing, and India's IT Act & CERT-In guidelines." },
    { title:"Capstone Project",                        desc:"Real-world security assessment of a simulated national security environment — evaluated by CRENS faculty and CERT-In experts." },
  ];
  // module_desc from the API is HTML — flagged with html:true so the renderer below knows to use dangerouslySetInnerHTML
  const modules = apiData?.curriculum_module?.length
    ? apiData.curriculum_module.map(m => ({ title: m.module_title, desc: m.module_desc, html:true }))
    : staticModules.map(m => ({ ...m, html:false }));

  // ── FAQs ── (faq_desc from the API is HTML)
  const faqTitle = apiData?.faq_title || "Frequently Asked Questions";
  const staticFaqs = [
    { q:"Who is this course designed for?",   a:"Defence personnel, government officials, PSU engineers, security analysts, and researchers working in or transitioning to national security roles." },
    { q:"What are the prerequisites?",         a:"Basic IT literacy is sufficient. No prior cybersecurity experience required. A brief orientation module is included for newcomers." },
    { q:"How is the course delivered?",        a:"8 weeks of pre-recorded lectures + weekly live sessions with subject experts, delivered on the Cyberkshetra platform by CERT-In." },
    { q:"What certificate will I receive?",    a:"Participants who successfully complete all modules and the capstone project receive a joint certificate from CRENS (BITS Pilani) and CERT-In (MeitY)." },
    { q:"What was the Batch 1 experience?",    a:"90 professionals from DRDO, Armed Forces, ISRO, and MHA completed Batch 1. 5 invited talks by leading experts including the Director General of CERT-In enriched the curriculum." },
  ];
  const faqs = apiData?.faqs?.length
    ? apiData.faqs.map(f => ({ q: f.faq_title, a: f.faq_desc, html:true }))
    : staticFaqs.map(f => ({ ...f, html:false }));

  // ── Industry-Recognized Certificate ──
  const industryTitle = apiData?.industry_title || "Industry-Recognized Certificate Jointly Issued by CERT-In & BITS Pilani";
  const industryDesc = apiData?.industry_desc
    || "Gain a prestigious cybersecurity certificate designed with insights from leading national bodies, ensuring it holds real industry value. This credential is more than an achievement—it's proof of your expertise, a trust signal for employers, and a powerful boost to your professional profile.";
  const industryImage = mediaUrl(apiData?.industry_image) || "https://placehold.co/700x460/FFFFFF/1A3A8F?text=Sample+Certificate";

  // ── Master Industry-Relevant Skills (cybersecurity_skill_desc is an HTML <ol> list) ──
  const skillSectionTitle = apiData?.cybersecurity_skill_title || "Master Industry-Relevant Skills With Leading Cybersecurity Tools";
  const skillSectionDescHtml = apiData?.cybersecurity_skill_desc || null;
  const skillSectionFallbackList = [
    "Gain practical experience with the cybersecurity concepts, techniques, and tools used by security professionals.",
    "Build hands-on expertise through guided labs, real-world scenarios, and immersive cyber range exercises.",
    "Learn to identify, analyze, and respond to modern cyber threats using industry-standard tools and technologies.",
  ];

  // ── CyberKshetra section (cyberkshetra_desc HTML holds both the intro paragraph and the feature list) ──
  const cyberkshetraTitle = apiData?.cyberkshetra_title || "Get Hands-On Training on India's Most Advanced Cyber Range";
  const cyberkshetraDescHtml = apiData?.cyberkshetra_desc || null;
  const cyberkshetraLogo = mediaUrl(apiData?.cyberkshetra_logo);
  const cyberkshetraImage1 = mediaUrl(apiData?.cyberkshetra_image1) || "https://placehold.co/460x300/1A2333/38BDF8?text=CyberKshetra+Overview";
  const cyberkshetraImage2 = mediaUrl(apiData?.cyberkshetra_image2) || "https://placehold.co/460x300/0F172A/38BDF8?text=Tech+Innovations+Summit";

  // ── Coming Next (next cohort teaser) ──
  const comingNextTitle = apiData?.coming_next_title || "Coming Next: Course on Quantum Communications";
  const comingNextDesc = apiData?.coming_next_desc || "Quantum Key Distribution · Post-Quantum Cryptography · Quantum Networks";

  const handleInput = (k, v) => {
    setForm(f => ({ ...f, [k]: v }));
    setFieldErrors(fe => (fe[k] ? { ...fe, [k]: null } : fe));
  };

  const validate = () => {
    const errors = {};
    if (!form.name.trim()) errors.name = "Full name is required.";
    if (!form.org.trim()) errors.org = "Organisation / Institution is required.";
    if (!form.email.trim()) {
      errors.email = "Email address is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      errors.email = "Please enter a valid email address.";
    }
    if (!form.phone.trim()) {
      errors.phone = "Phone number is required.";
    } else if (!/^\d{10}$/.test(form.phone.trim())) {
      errors.phone = "Please enter a valid 10-digit phone number.";
    }
    return errors;
  };

  const handleSubmit = () => {
    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    setSubmitError(null);
    setSubmitting(true);

    fetch(TRAINING_INTEREST_API_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        full_name: form.name,
        organisation: form.org,
        organisation_name: form.org,
        org_name: form.org,
        organization: form.org,
        organization_name: form.org,
        institution: form.org,
        designation: form.desig,
        email: form.email,
        phone: form.phone,
        phone_number: form.phone,
        mobile: form.phone,
        organisation_type: form.orgType,
        organization_type: form.orgType,
        org_type: form.orgType,
        course_slug: "professional-development-course-in-cybersecurity",
        course: "professional-development-course-in-cybersecurity",
      }),
    })
      .then(res =>
        res.json()
          .catch(() => ({}))
          .then(data => ({ ok: res.ok, status: res.status, data }))
      )
      .then(({ ok, status, data }) => {
        if (!ok || data?.status === false) {
          throw new Error(data?.message || `Request failed (${status})`);
        }
        setSubmitted(true);
        setForm({ name:"", org:"", desig:"", email:"", phone:"", orgType:"" });
      })
      .catch(err => {
        setSubmitError(err.message || "Something went wrong submitting your application. Please try again, or email us directly.");
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const sectionH2 = { fontFamily:"var(--font-heading)", fontWeight:800, fontSize:"1.15rem", color:C.navyDark, textTransform:"uppercase", letterSpacing:0.3, lineHeight:1.35 };
  const sectionEyebrow = { fontFamily:"Arial,Helvetica,sans-serif", fontSize:11, fontWeight:700, letterSpacing:1.5, textTransform:"uppercase", color:C.textMuted, marginBottom:6 };

  return (
    <div className="page-fade">
      {/* Scoped responsive rules — the grid/tool/gallery className hooks below had no CSS
          behind them before, so nothing actually reflowed on small screens. This adds it. */}
      <style>{`
        @media (max-width: 1024px) {
          .course-detail-grid { grid-template-columns: 1fr !important; }
        }
        .peek-track { scrollbar-width: none; -ms-overflow-style: none; }
        .peek-track::-webkit-scrollbar { display: none; height: 0; }
        .api-html-content p { margin: 0 0 12px; }
        .api-html-content p:last-child { margin-bottom: 0; }
        .api-html-content ul, .api-html-content ol { margin: 0 0 12px; padding-left: 20px; }
        .api-html-content li { margin-bottom: 6px; }
        .api-html-content strong { color: ${C.navyDark}; }
        @media (max-width: 900px) {
          .course-detail-wrap { padding: 32px 20px !important; }
          .breadcrumb-bar { padding: 12px 20px !important; }
          .coming-next-bar { padding: 28px 20px !important; }
          .grid-2 { grid-template-columns: 1fr !important; }
          .grid-3 { grid-template-columns: repeat(2, 1fr) !important; }
          .tools-grid { grid-template-columns: repeat(3, 1fr) !important; }
        }
        @media (max-width: 640px) {
          .course-detail-wrap { padding: 24px 16px !important; }
          .coming-next-bar { padding: 24px 16px !important; }
          .coming-next-inner { flex-direction: column !important; align-items: flex-start !important; }
          .grid-3 { grid-template-columns: 1fr !important; }
          .tools-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .success-gallery-card { flex-basis: calc(85% - 9px) !important; }
          .faculty-card { flex-basis: calc(90% - 9px) !important; }
        }
        @media (max-width: 480px) {
          .tools-grid { grid-template-columns: repeat(2, 1fr) !important; }
        }
      `}</style>

      {/* Breadcrumb */}
      <div className="breadcrumb-bar" style={{background:C.white,borderBottom:`1px solid ${C.border}`,padding:"12px 40px"}}>
        <div style={{maxWidth:1260,margin:"0 auto",display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
          <button onClick={onBack} style={{background:"none",border:"none",cursor:"pointer",fontFamily:"Arial,Helvetica,sans-serif",fontSize:13,color:C.textMuted,padding:0,display:"flex",alignItems:"center",gap:4}}>
            ← Training
          </button>
          <span style={{color:C.borderLight}}>›</span>
          <span style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:13,color:C.textMid}}>{heroTitle}</span>
        </div>
      </div>
<div
  ref={gridRef}
  className="course-detail-wrap course-detail-grid"
  style={{
    maxWidth: 1260,
    margin: "0 auto",
    padding: "48px 40px",
    display: "grid",
    gridTemplateColumns: "1fr 360px",
    gap: 48,
    alignItems: "start",
    backgroundColor: "#ffffff",
  }}
>

        {/* LEFT COLUMN */}
        <div style={{minWidth:0}}>
          {/* Hero */}
          <div style={{marginBottom:28}}>
            <div style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:11,fontWeight:700,letterSpacing:2,textTransform:"uppercase",color:C.gold,marginBottom:12}}>{badgeText}</div>
            <h1 style={{fontFamily:"var(--font-heading)",fontWeight:800,fontSize:"clamp(1.6rem,5vw,2.6rem)",color:C.navyDark,lineHeight:1.15,marginBottom:16}}>{heroTitle}</h1>
            <p style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:16,color:C.textMid,lineHeight:1.7,marginBottom:24}}>{heroSubtitle}</p>
            <div style={{display:"flex",gap:10,flexWrap:"wrap",marginBottom:20}}>
              {partners.map((p,i) => (
                <span key={i} style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:13,fontWeight:500,color:C.navyDark,border:`1px solid ${C.border}`,borderRadius:6,padding:"6px 14px",background:C.white}}>{p}</span>
              ))}
            </div>
            <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
              {highlightTags.map((h,i) => (
                <span key={i} style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:12.5,fontWeight:500,color:C.textMid,background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:20,padding:"6px 14px"}}>{h}</span>
              ))}
            </div>
          </div>

          {/* Academia/Government/Industry intro + photo collage */}
          <div className="grid-2" style={{display:"grid",gridTemplateColumns:"1.1fr 1fr",gap:32,alignItems:"start",marginBottom:44}}>
            <div>
              <h2 style={{...sectionH2,fontSize:"1.35rem",marginBottom:14}}>{whySectionTitle}</h2>
              <div style={{display:"flex",alignItems:"center",flexWrap:"wrap",gap:12,marginBottom:16}}>
                {whySectionLogos.map((item,i) => (
                  <div key={i} style={{display:"flex",alignItems:"center",gap:12}}>
                    {item.image
                      ? <img src={item.image} alt="" style={{height:24,width:"auto",maxWidth:120,objectFit:"contain"}}/>
                      : <span style={{fontFamily:"var(--font-heading)",fontWeight:800,fontSize:15,color:C.navyDark}}>{item.name}</span>}
                    {i < whySectionLogos.length-1 && <span style={{color:C.borderLight,fontSize:16}}>|</span>}
                  </div>
                ))}
              </div>
              {whySectionDescIsHtml ? (
                <div className="api-html-content" style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:14.5,color:C.textMid,lineHeight:1.9}} dangerouslySetInnerHTML={{__html: whySectionDesc}} />
              ) : (
                <p style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:14.5,color:C.textMid,lineHeight:1.9}}>{whySectionDesc}</p>
              )}
            </div>
            {/* Static overlapping photo collage — no slider controls, per request */}
           
<div style={{position:"relative",width:"100%",height:380}}>
  <img src={introGallery[0].src} alt="" style={{width:"100%",height:"100%",objectFit:"cover",borderRadius:10,boxShadow:"0 12px 28px rgba(15,37,87,0.18)"}}/>
</div>
          </div>

          {/* Cybersecurity: A Career in High Demand */}
          <div style={{background:"#f7faff",borderRadius:12,padding:"28px 32px",marginBottom:44}}>
            <div style={sectionEyebrow}>Cybersecurity:</div>
            <h2 style={{...sectionH2,fontSize:"1.3rem",marginBottom:6,textTransform:"none"}}>A Career in High Demand</h2>
            <p style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:14,color:C.textMid,marginBottom:18}}>Cybersecurity jobs are surging worldwide, offering high salaries and unmatched career stability.</p>
            <div className="grid-3" style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:14,marginBottom:20}}>
              {stats.map(([n,l],i) => (
                <div key={i} style={{background:statBgs[i % statBgs.length],border:`1.5px solid ${statColors[i % statColors.length]}`,borderRadius:10,padding:"16px 18px"}}>
                  <div style={{fontFamily:"var(--font-heading)",fontWeight:800,fontSize:20,color:statColors[i % statColors.length],marginBottom:6}}>{n}</div>
                  <div style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:12.5,color:C.textMid,lineHeight:1.5}}>{l}</div>
                </div>
              ))}
            </div>
            <div style={{fontFamily:"var(--font-heading)",fontWeight:700,fontSize:14,color:C.navyDark,marginBottom:8,textTransform:"uppercase",letterSpacing:0.3}}>Ready to Upskill or Advance in Cybersecurity?</div>
            <p style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:13.5,color:C.textMid,lineHeight:1.8,margin:0}}>
              Learn cybersecurity from basic to advanced levels with hands-on labs and real-world scenarios. No prior coding or IT experience is required.
              {" "}<strong>Just the willingness to start your journey into a high-demand field.</strong>
            </p>
          </div>

          {/* Who Is This Program For */}
          <div style={{marginBottom:44}}>
            <h2 style={{...sectionH2,textTransform:"none",fontSize:"1.3rem",marginBottom:20}}>Who Is This Program For?</h2>
            <div className="grid-3" style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:14}}>
              {attends.map((a,i) => (
                <div key={i} style={{background:C.white,border:`1px solid ${C.border}`,borderRadius:10,padding:"18px 18px 16px",transition:"border-color .2s,box-shadow .2s"}}
                  onMouseEnter={e=>{e.currentTarget.style.borderColor=C.accentDeep;e.currentTarget.style.boxShadow="0 4px 14px rgba(15,37,87,0.08)";}}
                  onMouseLeave={e=>{e.currentTarget.style.borderColor=C.border;e.currentTarget.style.boxShadow="none";}}>
                  {a.image ? (
                    <img src={a.image} alt={a.title} style={{width:28,height:28,objectFit:"contain",marginBottom:10}}/>
                  ) : (
                    <div style={{fontSize:22,marginBottom:10}}>{a.icon}</div>
                  )}
                  <div style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:13.5,fontWeight:700,color:C.navyDark,marginBottom:6}}>{a.title}</div>
                  <div style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:12.5,color:C.textMuted,lineHeight:1.55}}>{a.desc}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Your Career Path After Certification */}
          <div style={{marginBottom:44}}>
            <div style={sectionEyebrow}>{careerEyebrow}</div>
            <h2 style={{...sectionH2,fontSize:"1.3rem",marginBottom:14,textTransform:"none"}}>{careerHeading}</h2>
            {careerDescParas.map((p,i) => (
              <p key={i} style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:14,color:C.textMid,lineHeight:1.85,marginBottom: i < careerDescParas.length-1 ? 6 : 18}}>{p}</p>
            ))}
            <div style={{display:"flex",flexWrap:"wrap",gap:10}}>
              {careerPaths.map((c,i) => (
                <span key={i} style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:13,fontWeight:500,color:C.navyDark,border:`1.5px solid ${c.highlight ? "#16A34A" : C.border}`,borderRadius:20,padding:"8px 18px",background:C.white}}>{c.label}</span>
              ))}
            </div>
          </div>

          {/* World-Class Faculty — auto-advances every 5s, pauses on hover/touch */}
          <div style={{background:"#eaf8ff",borderRadius:12,padding:"28px 32px",marginBottom:44}}>
            <div style={sectionEyebrow}>{facultyHeadingLine1}</div>
            <h2 style={{...sectionH2,fontSize:"1.3rem",marginBottom:20,textTransform:"none"}}>{facultyHeadingLine2}</h2>
            <PeekSlider items={faculty} cardClassName="faculty-card" autoPlay interval={5000} renderItem={(f) => (
              <div style={{background:C.white,borderRadius:10,padding:18,display:"flex",gap:14,height:"100%",boxSizing:"border-box",position:"relative"}}>
                <img src={f.photo} alt={f.name} style={{width:64,height:64,borderRadius:"50%",objectFit:"cover",flexShrink:0}}/>
                <div style={{paddingBottom:22}}>
                  <div style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:14.5,fontWeight:700,color:C.navyDark}}>{f.name}</div>
                  <div style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:12,color:C.textMuted,marginBottom:8,lineHeight:1.4}}>{f.title}</div>
                  <p style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:11.5,color:C.textMid,lineHeight:1.6,fontStyle:"italic",margin:0}}>{f.bio}</p>
                </div>
                {/* Company logo, bottom-right of the card, as in the reference screenshot.
                    Pulled from this faculty member's company_logo field when available. */}
                {f.companyLogo ? (
                  <img src={f.companyLogo} alt="Company logo" style={{position:"absolute",bottom:14,right:16,height:20,maxWidth:120,width:"auto",objectFit:"contain"}}/>
                ) : (
                  <div style={{position:"absolute",bottom:14,right:16,display:"flex",alignItems:"center",gap:5}}>
                    <div style={{width:16,height:16,borderRadius:"50%",background:"conic-gradient(#2563EB 0deg 180deg,#D4A017 180deg 360deg)",flexShrink:0}}/>
                    <div style={{lineHeight:1.1}}>
                      <div style={{fontFamily:"var(--font-heading)",fontWeight:800,fontSize:11,color:C.navyDark}}>BITS Pilani</div>
                      <div style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:6.5,color:C.textMuted,letterSpacing:0.2}}>Pilani | Dubai | Goa | Hyderabad</div>
                    </div>
                  </div>
                )}
              </div>
            )}/>
          </div>

          {/* Course Curriculum */}
          <div style={{marginBottom:44}}>
            <h2 style={{...sectionH2,fontSize:"1.3rem",marginBottom:6,textTransform:"none"}}>{curriculumTitle}</h2>
            <p style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:13.5,color:C.textMuted,marginBottom:20}}>{curriculumDesc}</p>
            <div style={{border:`1px solid ${C.border}`,borderRadius:10,overflow:"hidden"}}>
              {modules.map((m,i) => (
                <div key={i} style={{borderBottom: i < modules.length-1 ? `1px solid ${C.borderLight}` : "none"}}>
                  <button onClick={() => setOpenModule(openModule===i?null:i)}
                    style={{width:"100%",textAlign:"left",background:openModule===i?C.bgAlt:C.white,border:"none",cursor:"pointer",padding:"16px 20px",display:"flex",justifyContent:"space-between",alignItems:"center",fontFamily:"Arial,Helvetica,sans-serif",fontSize:14,fontWeight:600,color:C.navyDark,transition:"background #eaf8ff"}}>
                    {m.title}
                    <span style={{color:C.textMuted,fontSize:16,flexShrink:0,marginLeft:12,transform:openModule===i?"rotate(180deg)":"rotate(0deg)",transition:"transform .2s"}}>⌄</span>
                  </button>
                  {openModule===i && (
                    m.html
                      ? <div className="api-html-content" style={{padding:"4px 20px 18px",fontFamily:"Arial,Helvetica,sans-serif",fontSize:13.5,color:C.textMid,lineHeight:1.75,background:C.bgAlt}} dangerouslySetInnerHTML={{__html: m.desc}} />
                      : <div style={{padding:"4px 20px 18px",fontFamily:"Arial,Helvetica,sans-serif",fontSize:13.5,color:C.textMid,lineHeight:1.75,background:C.bgAlt}}>{m.desc}</div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* How the Program is Delivered */}
          <div style={{marginBottom:44}}>
            <h2 style={{...sectionH2,fontSize:"1.3rem",marginBottom:20,textTransform:"none"}}>{deliveryTitle}</h2>
            <div className="grid-3" style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:14,marginBottom:20}}>
              {deliveryFeatures.map((d,i) => (
                <div key={i} style={{background:C.white,border:`1px solid ${C.border}`,borderRadius:10,padding:"20px 18px"}}>
                  {d.image
                    ? <img src={d.image} alt="" style={{width:32,height:32,objectFit:"contain",marginBottom:10}}/>
                    : <div style={{fontSize:22,marginBottom:10}}>{d.icon}</div>}
                  <div style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:13.5,fontWeight:700,color:C.navyDark,marginBottom:6}}>{d.title}</div>
                  <div style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:12.5,color:C.textMuted,lineHeight:1.55}}>{d.desc}</div>
                </div>
              ))}
            </div>
            <div style={{background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:10,padding:"16px 20px"}}>
              <p style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:13.5,color:C.textMid,lineHeight:1.7,margin:0}}>{invitedTalksNote}</p>
            </div>
          </div>

          {/* Participants Profile From Previous Batches */}
          <div style={{marginBottom:44}}>
            <div style={sectionEyebrow}>Participants Profile From</div>
            <h2 style={{...sectionH2,fontSize:"1.3rem",marginBottom:20,textTransform:"none"}}>Previous Batches</h2>
            <div className="grid-2" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
              <div style={{background:C.white,border:`1px solid ${C.border}`,borderRadius:10,padding:"20px 24px"}}>
                <div style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:13,fontWeight:600,color:C.textMid,marginBottom:16}}>By Organisation Type</div>
                <DonutChartV2 segments={[
                  {v:32, c:"#0F2557", label:"Defence"},
                  {v:30, c:"#2563EB", label:"Government"},
                  {v:25, c:"#D4A017", label:"PSU"},
                  {v:13, c:"#DC2626", label:"Private"},
                ]}/>
              </div>
              <div style={{background:C.white,border:`1px solid ${C.border}`,borderRadius:10,padding:"20px 24px"}}>
                <div style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:13,fontWeight:600,color:C.textMid,marginBottom:16}}>By Parent Agency</div>
                <DonutChartV2 segments={[
                  {v:38, c:"#0F2557", label:"DRDO"},
                  {v:22, c:"#2563EB", label:"Armed Forces"},
                  {v:20, c:"#D4A017", label:"ISRO"},
                  {v:20, c:"#94A3B8", label:"MHA/Others"},
                ]}/>
              </div>
            </div>
          </div>

          {/* Gallery of Success Stories — peeking, auto-advancing slider fed by the API's gallery images */}
          <div style={{background:"#f6f7fe",borderRadius:12,padding:"28px 32px",marginBottom:44}}>
            <div style={sectionEyebrow}>Gallery of</div>
            <h2 style={{...sectionH2,fontSize:"1.3rem",marginBottom:18,textTransform:"none"}}>Success Stories</h2>
            <PeekSlider
              items={successGallery}
              cardWidth="calc(33.333% - 8px)"
              cardClassName="success-gallery-card"
              autoPlay
              interval={4000}
              renderItem={(src) => (
                <div style={{borderRadius:10,overflow:"hidden",height:170}}>
                  <img src={src} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                </div>
              )}
            />
          </div>

          {/* Our Community Speaks — clicking a video opens it in a popup lightbox */}
          <div className="grid-2" style={{display:"grid",gridTemplateColumns:"1fr 2.2fr",gap:32,alignItems:"center",marginBottom:44}}>
            <div>
              <div style={sectionEyebrow}>Our Community</div>
              <h2 style={{...sectionH2,fontSize:"1.3rem",textTransform:"none"}}>{videoHeading}</h2>
            </div>
            <Carousel items={testimonials} height={340} dots renderItem={(t) => (
              <div onClick={() => t.youtubeId && setVideoModal(t.youtubeId)} style={{width:"100%",height:"100%",position:"relative",cursor:t.youtubeId?"pointer":"default"}}>
                <img src={t.thumb} alt="Testimonial video" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center"}}>
                  <div style={{width:64,height:64,borderRadius:"50%",background:"rgba(220,38,38,0.92)",display:"flex",alignItems:"center",justifyContent:"center",boxShadow:"0 4px 16px rgba(0,0,0,0.35)"}}>
                    <div style={{width:0,height:0,borderTop:"12px solid transparent",borderBottom:"12px solid transparent",borderLeft:"20px solid #fff",marginLeft:5}}/>
                  </div>
                </div>
              </div>
            )}/>
          </div>

          {/* Industry-Recognized Certificate */}
          <div className="grid-2" style={{display:"grid",gridTemplateColumns:"1fr 1.1fr",gap:32,alignItems:"center",marginBottom:44}}>
            <div>
              <div style={sectionEyebrow}>Industry-Recognized Certificate</div>
              <h2 style={{...sectionH2,fontSize:"1.25rem",marginBottom:14,textTransform:"none"}}>{industryTitle}</h2>
              <p style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:14,color:C.textMid,lineHeight:1.85,marginBottom:20}}>{industryDesc}</p>
              <div style={{display:"flex",alignItems:"center",gap:16,flexWrap:"wrap"}}>
             
               <span
  style={{
    fontFamily: "Arial,Helvetica,sans-serif",
    fontSize: 14,
    fontWeight: 600,
    color: "#d39f17",
    borderLeft: `2px solid ${C.border}`,
    paddingLeft: 12,
  }}
>
  {batchNote}
</span>
              </div>
            </div>
            <img src={industryImage} alt="Sample certificate" style={{width:"100%",borderRadius:10,border:`1px solid ${C.border}`}}/>
          </div>

          {/* Master Industry-Relevant Skills */}
          <div style={{padding:"36px 0",marginBottom:44}}>
            <h2 style={{fontFamily:"var(--font-heading)",fontWeight:800,fontSize:"1.4rem",color:"#002B5B",textTransform:"uppercase",letterSpacing:0.3,marginBottom:20}}>{skillSectionTitle}</h2>
            {skillSectionDescHtml ? (
              <div className="api-html-content" style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:14.5,color:"#3A4A5C",marginBottom:28}} dangerouslySetInnerHTML={{__html: skillSectionDescHtml}} />
            ) : (
              <ol style={{margin:"0 0 28px",paddingLeft:20,display:"flex",flexDirection:"column",gap:8}}>
                {skillSectionFallbackList.map((t,i) => (
                  <li key={i} style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:14.5,color:"#3A4A5C",lineHeight:1.8}}>{t}</li>
                ))}
              </ol>
            )}

            {/* TOOLS */}
            <div style={{fontFamily:"var(--font-heading)",fontWeight:800,fontSize:15,color:"#002B5B",marginBottom:16,textTransform:"uppercase",letterSpacing:0.5}}>Tools</div>
            <div style={{background:"#fbf1f1",borderRadius:16,padding:"28px",marginBottom:32}}>
              <div className="tools-grid" style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:16}}>
                {toolsList.map((t,i) => (
                  <div key={i}
                    style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8,height:80,background:"#FFFFFF",border:"1px solid #D6E4F0",borderRadius:14,boxShadow:"0 2px 8px rgba(0,43,91,0.06)",padding:"0 14px",transition:"transform .18s, box-shadow .18s"}}
                    onMouseEnter={e=>{e.currentTarget.style.transform="translateY(-3px)";e.currentTarget.style.boxShadow="0 8px 18px rgba(0,43,91,0.12)";}}
                    onMouseLeave={e=>{e.currentTarget.style.transform="translateY(0)";e.currentTarget.style.boxShadow="0 2px 8px rgba(0,43,91,0.06)";}}>
                    {t.logo
                      ? <img src={t.logo} alt={t.name || "tool"} style={{height:t.name?24:40,width:"auto",maxWidth:"100%",objectFit:"contain"}}/>
                      : <span style={{fontSize:20}}>{t.icon}</span>}
                    {t.name && <span style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:13,fontWeight:800,letterSpacing:0.2,color:t.color}}>{t.name}</span>}
                  </div>
                ))}
              </div>
            </div>

            {/* SKILLS */}
            <div style={{fontFamily:"var(--font-heading)",fontWeight:800,fontSize:15,color:"#002B5B",marginBottom:16,textTransform:"uppercase",letterSpacing:0.5}}>Skills</div>
            <div style={{background:"#fbf1f1",borderRadius:16,padding:"28px"}}>
              <div style={{display:"flex",flexWrap:"wrap",gap:12}}>
                {skillsList.map((s,i) => (
                  <span key={i}
                    style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:13,fontWeight:600,color:"#002B5B",background:"#FFFFFF",border:"1.5px solid #d4d8d9",borderRadius:22,padding:"10px 10px",cursor:"default",transition:"background .18s, color .18s"}}
                    onMouseEnter={e=>{e.currentTarget.style.background="#0EA5E9";e.currentTarget.style.color="#FFFFFF";}}
                    onMouseLeave={e=>{e.currentTarget.style.background="#FFFFFF";e.currentTarget.style.color="#002B5B";}}>
                    {s}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* On-Campus Graduation Ceremony */}
          <div className="grid-2" style={{display:"grid",gridTemplateColumns:"1fr 1.1fr",gap:32,alignItems:"center",marginBottom:44}}>
            <Carousel items={graduationGallery} height={280} renderItem={(src) => (
              <img src={src} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
            )}/>
            <div>
              <div style={sectionEyebrow}>On-Campus Graduation Ceremony</div>
              <h2 style={{...sectionH2,fontSize:"1.2rem",marginBottom:14,textTransform:"none"}}>{campusTitle}</h2>
              <p style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:13.5,color:C.textMid,lineHeight:1.85,marginBottom:16}}>{campusDesc}</p>
              <div style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:13,fontWeight:700,color:C.navyDark}}>{campusSubheading}</div>
            </div>
          </div>

          {/* Speakers from India's Most Prestigious Organizations */}
          <div className="grid-2" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:32,marginBottom:44,background:"#f9f9f9",padding: "28px 32px"}}>
            <div>
              <div style={sectionEyebrow}>{organizationTitleLine1}</div>
              <h2 style={{...sectionH2,fontSize:"1.2rem",marginBottom:14,textTransform:"none"}}>{organizationTitleLine2}</h2>
              {organizationDescHtml ? (
                <div className="api-html-content" style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:13.5,color:C.textMid,lineHeight:1.85}} dangerouslySetInnerHTML={{__html: organizationDescHtml}} />
              ) : (
                <>
                  <p style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:13.5,color:C.textMid,lineHeight:1.85,marginBottom:18}}>
                    Learn from industry leaders and government experts through guest sessions that reveal the latest cybersecurity trends and real-world insights.
                  </p>
                  <div style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:13,fontWeight:700,color:C.navyDark,marginBottom:10}}>Topics Covered</div>
                  <ul style={{margin:0,paddingLeft:18,display:"flex",flexDirection:"column",gap:6}}>
                    {["Digital Forensics and crime investigation","Digital Twins & Cyber Ranges for Space Cyber Security","APT linked malware","Cyber warfare in age of AI","VShell- Cyber Warfare Tool for Global Targeting"].map((t,i) => (
                      <li key={i} style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:13,color:C.textMid,lineHeight:1.6}}>{t}</li>
                    ))}
                  </ul>
                </>
              )}
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,alignContent:"start"}}>
              {speakerLogos.map((s,i) => (
                <div key={i} style={{background:C.white,border:`1px solid ${C.border}`,borderRadius:10,padding:"22px 16px",display:"flex",alignItems:"center",justifyContent:"center",textAlign:"center"}}>
                  {s.image
                    ? <img src={s.image} alt="" style={{maxWidth:"100%",maxHeight:36,objectFit:"contain"}}/>
                    : <span style={{fontFamily:"Arial,Helvetica,sans-serif",fontWeight:700,fontSize:14,color:C.navyDark}}>{s.name}</span>}
                </div>
              ))}
            </div>
          </div>

          {/* CyberKshetra hands-on training */}
          <div style={{marginBottom:44}}>
            <div className="grid-2" style={{display:"grid",gridTemplateColumns:"1fr 1.15fr",gap:40,alignItems:"center"}}>
              <div style={{position:"relative",minHeight:280}}>
                <img src={cyberkshetraImage1} alt="CyberKshetra platform overview"
                  style={{position:"absolute",top:0,left:0,width:"78%",borderRadius:10,border:`1px solid ${C.border}`,boxShadow:"0 12px 30px rgba(15,37,87,0.16)"}}/>
                <img src={cyberkshetraImage2} alt="CyberKshetra event dashboard"
                  style={{position:"absolute",bottom:0,right:0,width:"78%",borderRadius:10,border:`1px solid ${C.border}`,boxShadow:"0 16px 34px rgba(15,37,87,0.2)"}}/>
              </div>
              <div>
                <div style={{display:"flex",alignItems:"center",gap:8,fontFamily:"var(--font-heading)",fontWeight:800,fontSize:16,letterSpacing:0.3,marginBottom:14,color:C.navyDark}}>
                  {cyberkshetraLogo
                    ? <img src={cyberkshetraLogo} alt="" style={{height:22,width:"auto"}}/>
                    : <span style={{fontSize:20}}>🛡</span>} 
                </div>
                <h2 style={{fontFamily:"var(--font-heading)",fontWeight:800,fontSize:"1.3rem",lineHeight:1.35,marginBottom:14,textTransform:"uppercase",color:C.navyDark}}>{cyberkshetraTitle}</h2>
                {cyberkshetraDescHtml ? (
                  <div className="api-html-content" style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:14,color:C.textMid,lineHeight:1.85}} dangerouslySetInnerHTML={{__html: cyberkshetraDescHtml}} />
                ) : (
                  <>
                    <p style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:14,color:C.textMid,lineHeight:1.85,marginBottom:20}}>
                      CyberKshetra, an advanced cyber range platform developed in India, offers a robust environment for training, testing, and skill enhancement. This comprehensive platform equips you to excel in every phase of DevSecOps, helping you stay ahead of evolving cyber threats and sophisticated attack patterns, including Advanced Persistent Threats (APTs).
                    </p>
                    <div style={{fontFamily:"var(--font-heading)",fontWeight:800,fontSize:14,marginBottom:12,color:C.navyDark,textTransform:"uppercase",letterSpacing:0.3}}>Key Features You'll Experience:</div>
                    <ul style={{margin:0,paddingLeft:0,listStyle:"none",display:"flex",flexDirection:"column",gap:10}}>
                      {cyberkshetraFeaturesFallback.map((f,i) => (
                        <li key={i} style={{display:"flex",gap:10,alignItems:"flex-start",fontFamily:"Arial,Helvetica,sans-serif",fontSize:14,color:C.navyDark,lineHeight:1.5}}>
                          <span style={{color:C.navyDark,flexShrink:0}}>★</span>{f}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* The Time to Invest in Your Career Is Now */}
          <div style={{marginBottom:44}}>
            <h2 style={{...sectionH2,fontSize:"1.2rem",marginBottom:6,textTransform:"none"}}>{feeTitle}</h2>
            <p style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:13.5,color:C.textMid,marginBottom:18}}>{feeSectionDesc}</p>
            <div style={{background:C.navy,borderRadius:12,padding:"28px 32px",display:"flex",alignItems:"center",gap:32,flexWrap:"wrap"}}>
              <div style={{background:C.white,borderRadius:10,padding:"18px 26px"}}>
                <div style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:11,color:C.textMuted,marginBottom:4}}>*Including GST</div>
                <div style={{fontFamily:"var(--font-heading)",fontWeight:800,fontSize:28,color:C.navyDark}}>₹ {feeAmount}</div>
                <div style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:11.5,color:C.textMuted,marginTop:4}}>{batchNote}</div>
              </div>
              <div>
                <div style={{fontFamily:"var(--font-heading)",fontWeight:700,fontSize:16,color:"#fff",marginBottom:6}}>Early Enrolment Fee</div>
                <div style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:13,color:"rgba(255,255,255,0.75)"}}>{feeBoxNote}</div>
              </div>
            </div>
          </div>

          {/* FAQ */}
          <div>
            <h2 style={{...sectionH2,fontSize:"1.3rem",marginBottom:20,textTransform:"none"}}>{faqTitle}</h2>
            <div style={{border:`1px solid ${C.border}`,borderRadius:10,overflow:"hidden"}}>
              {faqs.map((f,i) => (
                <div key={i} style={{borderBottom: i < faqs.length-1 ? `1px solid ${C.borderLight}` : "none"}}>
                  <button onClick={() => setOpenFaq(openFaq===i?null:i)}
                    style={{width:"100%",textAlign:"left",background:openFaq===i?C.bgAlt:C.white,border:"none",cursor:"pointer",padding:"16px 20px",display:"flex",justifyContent:"space-between",alignItems:"center",fontFamily:"Arial,Helvetica,sans-serif",fontSize:14,fontWeight:600,color:C.navyDark,transition:"background .15s"}}>
                    {f.q}
                    <span style={{color:C.textMuted,fontSize:16,flexShrink:0,marginLeft:12,transform:openFaq===i?"rotate(180deg)":"rotate(0deg)",transition:"transform .2s"}}>⌄</span>
                  </button>
                  {openFaq===i && (
                    f.html
                      ? <div className="api-html-content" style={{padding:"4px 20px 18px",fontFamily:"Arial,Helvetica,sans-serif",fontSize:13.5,color:C.textMid,lineHeight:1.75,background:C.bgAlt}} dangerouslySetInnerHTML={{__html: f.a}} />
                      : <div style={{padding:"4px 20px 18px",fontFamily:"Arial,Helvetica,sans-serif",fontSize:13.5,color:C.textMid,lineHeight:1.75,background:C.bgAlt}}>{f.a}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN — JS-driven sticky (see StickyPanel above), immune to any ancestor
            overflow:hidden that was breaking plain CSS position:sticky */}
        <StickyPanel boundsRef={gridRef} topOffset={80} disableBelow={1024}>
          <div style={{background:C.white,border:`1px solid ${C.border}`,borderRadius:14,overflow:"hidden",boxShadow:"0 4px 24px rgba(15,37,87,0.1)"}}>
              <div style={{background:C.navy,height:4}}/>
              <div style={{padding:"24px 24px 28px"}}>
                <h3 style={{fontFamily:"var(--font-heading)",fontWeight:700,fontSize:"1.15rem",color:C.navyDark,marginBottom:4}}>Register Your Interest</h3>
                <p style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:13,color:C.textMuted,marginBottom:24}}>Cohort 3 launching October — limited seats</p>
                {[
                  {label:"Full Name",key:"name",ph:"Your full name",req:true,type:"text"},
                  {label:"Organisation / Institution",key:"org",ph:"Your organisation",req:true,type:"text"},
                  {label:"Designation",key:"desig",ph:"Your designation",req:false,type:"text"},
                  {label:"Email Address",key:"email",ph:"your.email@org.in",req:true,type:"email"},
                  {label:"Phone Number",key:"phone",ph:"10-digit mobile number",req:true,type:"tel"},
                ].map(({label,key,ph,req,type}) => (
                  <div key={key} style={{marginBottom:16}}>
                    <label style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:12,fontWeight:600,color:C.navyDark,display:"block",marginBottom:0}}>
                      {label} {req && <span style={{color:"#C0392B"}}>*</span>}
                    </label>
                    <input type={type} placeholder={ph} value={form[key]} onChange={e=>handleInput(key,e.target.value)}
                      style={{width:"100%",padding:"10px 12px",border:`1px solid ${fieldErrors[key]?"#C0392B":C.border}`,borderRadius:6,fontFamily:"Arial,Helvetica,sans-serif",fontSize:13.5,color:C.text,outline:"none",transition:"border .2s",boxSizing:"border-box"}}
                      onFocus={e=>e.target.style.borderColor=fieldErrors[key]?"#C0392B":C.blue} onBlur={e=>e.target.style.borderColor=fieldErrors[key]?"#C0392B":C.border}/>
                    {fieldErrors[key] && (
                      <div style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:11.5,color:"#C0392B",marginTop:5}}>{fieldErrors[key]}</div>
                    )}
                  </div>
                ))}
                <div style={{marginBottom:22}}>
                  <label style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:12.5,fontWeight:600,color:C.navyDark,display:"block",marginBottom:6}}>Organisation Type</label>
                  <select value={form.orgType} onChange={e=>handleInput("orgType",e.target.value)}
                    style={{width:"100%",padding:"10px 12px",border:`1px solid ${C.border}`,borderRadius:6,fontFamily:"Arial,Helvetica,sans-serif",fontSize:13.5,color:form.orgType?C.text:C.textMuted,background:C.white,outline:"none",cursor:"pointer",boxSizing:"border-box"}}>
                    <option value="">Select type</option>
                    <option>Defence (Armed Forces)</option>
                    <option>Central Government</option>
                    <option>State Government</option>
                    <option>PSU</option>
                    <option>DRDO / ISRO / DAE</option>
                    <option>Private Industry</option>
                    <option>Academic Institution</option>
                  </select>
                </div>
                <button onClick={handleSubmit} disabled={submitting}
                  style={{width:"100%",background:C.navy,color:C.white,border:"none",borderRadius:8,padding:"14px 20px",fontFamily:"Arial,Helvetica,sans-serif",fontWeight:700,fontSize:14.5,cursor:submitting?"default":"pointer",transition:"background .2s",letterSpacing:0.2,opacity:submitting?0.7:1}}
                  onMouseEnter={e=>{if(!submitting)e.currentTarget.style.background=C.navyDark;}}
                  onMouseLeave={e=>{if(!submitting)e.currentTarget.style.background=C.navy;}}>
                  {submitting ? "Submitting…" : "Submit Application"}
                </button>
                {submitError && (
                  <p style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:12.5,color:"#C0392B",textAlign:"center",marginTop:12,lineHeight:1.6}}>{submitError}</p>
                )}
                <p style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:11.5,color:C.textMuted,textAlign:"center",marginTop:14,lineHeight:1.6}}>By submitting, you agree to be contacted by CRENS, BITS Pilani and its partners regarding this program.</p>
              </div>
            </div>
        </StickyPanel>
      </div>

      {/* Coming Next — full-width teaser bar for the next cohort, placed right before the footer */}
      <div className="coming-next-bar" style={{background:C.navy,padding:"36px 40px"}}>
        <div className="coming-next-inner" style={{maxWidth:1260,margin:"0 auto",display:"flex",alignItems:"center",justifyContent:"space-between",gap:24,flexWrap:"wrap"}}>
          <div>
            <h3 style={{fontFamily:"var(--font-heading)",fontWeight:700,fontSize:"1.15rem",color:C.white,margin:"0 0 6px"}}>{comingNextTitle}</h3>
            <p style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:13.5,color:"rgba(255,255,255,0.72)",margin:0}}>{comingNextDesc}</p>
          </div>
          <button
            style={{background:"transparent",color:C.white,border:"1.5px solid rgba(255,255,255,0.55)",borderRadius:6,padding:"12px 28px",fontFamily:"Arial,Helvetica,sans-serif",fontWeight:600,fontSize:14,cursor:"pointer",whiteSpace:"nowrap",transition:"background .2s"}}
            onMouseEnter={e=>{e.currentTarget.style.background="rgba(255,255,255,0.12)";}}
            onMouseLeave={e=>{e.currentTarget.style.background="transparent";}}
          >
            Register Interest
          </button>
        </div>
      </div>

      {/* Video lightbox — opens on top of everything when a testimonial thumbnail is clicked */}
      {videoModal && (
        <div
          onClick={() => setVideoModal(null)}
          style={{
            position:"fixed", inset:0, zIndex:600,
            background:"rgba(8,21,64,0.8)",
            display:"flex", alignItems:"center", justifyContent:"center",
            padding:20,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{position:"relative",width:"100%",maxWidth:860,aspectRatio:"16 / 9",background:"#000",borderRadius:12,overflow:"hidden",boxShadow:"0 24px 64px rgba(0,0,0,0.45)"}}
          >
            <button
              onClick={() => setVideoModal(null)}
              aria-label="Close video"
              style={{position:"absolute",top:10,right:10,zIndex:2,width:34,height:34,borderRadius:"50%",background:"rgba(0,0,0,0.6)",color:"#fff",border:"none",cursor:"pointer",fontSize:18,lineHeight:1,display:"flex",alignItems:"center",justifyContent:"center"}}
            >×</button>
            <iframe
              src={`https://www.youtube.com/embed/${videoModal}?autoplay=1`}
              title="Testimonial video"
              style={{width:"100%",height:"100%",border:"none"}}
              allow="autoplay; encrypted-media; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      )}

      {submitted && (
        <div
          onClick={() => setSubmitted(false)}
          style={{
            position:"fixed", inset:0, zIndex:500,
            background:"rgba(8,21,64,0.55)",
            display:"flex", alignItems:"center", justifyContent:"center",
            padding:20,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{background:C.white,borderRadius:16,padding:"48px 36px",textAlign:"center",boxShadow:"0 20px 60px rgba(0,0,0,0.3)",maxWidth:420,width:"100%"}}
          >
            <div style={{width:64,height:64,borderRadius:"50%",background:C.navy,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 20px",fontSize:28,color:C.white}}>✓</div>
            <h3 style={{fontFamily:"var(--font-heading)",fontWeight:700,fontSize:"1.3rem",color:C.navyDark,marginBottom:12}}>Application Submitted!</h3>
            <p style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:14.5,color:C.textMid,lineHeight:1.75,marginBottom:24}}>Thank you for registering your interest. The CRENS team will contact you with Cohort 2 details shortly.</p>
            <button
              onClick={() => setSubmitted(false)}
              style={{background:C.navy,color:C.white,border:"none",borderRadius:8,padding:"12px 28px",fontFamily:"Arial,Helvetica,sans-serif",fontWeight:700,fontSize:14,cursor:"pointer"}}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}